import React from 'react'

import { useForm } from 'react-hook-form'

import { PaddingInset } from '@cash-design-system/react'
import { CurrencyCode } from '@cash-web/protos/squareup/common/currency_codes.pb'
import { AtmPickerAmountBlocker } from '@cash-web/protos/squareup/franklin/blockers/atm_picker_amount_blocker.pb'
import { ArcadeButtonFromButtonDefaultProto } from '@cash-web/shared/ui-arcade-button'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationSetAmount } from '@cash-web/shared-data-access-api/react-query'
import { formatMoney } from '@cash-web/shared-feature-common'
import { MoneyPicker } from '@cash-web/shared-feature-form'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { BaseBlocker, BaseBlockerFormFields, BlockerProps, SubmitButton } from '../../BaseBlocker/BaseBlocker'

type Props = BlockerProps & {
  blocker: AtmPickerAmountBlocker
}

interface BlockerAtmPickerAmountFormFields extends BaseBlockerFormFields {
  amount: string
}

export function BlockerAtmPickerAmount({ blocker }: Props): React.ReactElement | null {
  const form = useForm<BlockerAtmPickerAmountFormFields>({ mode: 'onTouched' })
  const flow = useFlowContext()
  const {
    formState: { isSubmitting, isDirty, isValid },
  } = form

  const isSubmitButtonDisabled = isSubmitting || !(isDirty || isValid)

  const { handleBlockerResponse } = useBlockerResponseHandler()
  const { mutateAsync: mutateAmount } = useMutationSetAmount({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
    },
  })

  const onSubmit = async ({ amount }: BlockerAtmPickerAmountFormFields) => {
    const numberAmount = parseInt(amount, 10)
    const requestBody = { amount_result: { amount: { amount: numberAmount, currency_code: CurrencyCode.USD } } }
    await handleBlockerResponse({
      promisedBlockerUpdate: mutateAmount(requestBody),
      isResponseSuccess: response => {
        const blockerDescriptors = response.response_context?.scenario_plan?.blocker_descriptors
        return !!blockerDescriptors?.length && blockerDescriptors?.length > 0
      },
    })
  }

  const { atm_picker: atmPicker, keypad_picker: keypadPicker, primary_button: primaryButton } = blocker
  const { title, subtitle } = atmPicker ?? {}

  const options = atmPicker?.options
    ?.map(option => {
      if (option.preset_amount?.amount?.amount) {
        return {
          value: option.preset_amount?.amount.amount,
          label: formatMoney(
            {
              amount: option.preset_amount?.amount?.amount ?? 0,
              currency_code: option.preset_amount?.amount?.currency_code,
            },
            { hideDecimalsIfNoCents: true }
          ),
        }
      }

      return undefined
    })
    ?.filter(Boolean) as { value: number; label: string }[] | undefined

  const blockerPrimaryButton = primaryButton ? (
    <ArcadeButtonFromButtonDefaultProto
      key="submit-amount-picker-btn"
      buttonDefault={primaryButton}
      onClick={function (): void {
        onSubmit(form.getValues())
      }}
      disabled={isSubmitButtonDisabled}
    />
  ) : (
    <SubmitButton key="submit-amount-picker-btn" disabled={isSubmitButtonDisabled} />
  )

  return (
    <BaseBlocker
      mainText={title}
      secondaryText={subtitle}
      form={form}
      onSubmit={onSubmit}
      buttons={[blockerPrimaryButton]}
    >
      <PaddingInset>
        <MoneyPicker
          inputName="amount"
          minCustomInputAmount={keypadPicker?.minimum_amount?.amount ?? 0}
          maxCustomInputAmount={keypadPicker?.maximum_amount?.amount ?? 0}
          options={options}
          isRequired={true}
          displayCustomAmountOption={atmPicker?.options?.some(option => option.keypad) ?? false}
        />
      </PaddingInset>
    </BaseBlocker>
  )
}
